/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { css } from '@emotion/react'
import { Spin } from 'antd'
import { LoadingOutlined, DeleteOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import type { SchedulerFlight } from '../hooks/useSchedulerFlights'
import {
  getFlightStatusBadgeColor,
  getFlightStatusLabel,
  formatFlightDateRange,
} from '../utils/schedulerStatus'

const spinnerWhiteStyle = { fontSize: 14, color: colors.WHITE } as const
const spinnerNeutralStyle = { fontSize: 14, color: colors.NEUTRAL_50 } as const

interface FlightSwitcherDropdownProps {
  flights: SchedulerFlight[]
  currentFlightId: string | null
  loading: boolean
  onDeleteFlight?: (flightId: string) => Promise<void>
  onFlightSelect?: (flight: SchedulerFlight) => void
  onNewFlight?: () => void
  /** 'pill' = current rounded-pill trigger (default), 'inline' = rectangular trigger for combined layout */
  variant?: 'pill' | 'inline'
}

const DropdownArrowIcon: React.FC<{
  isOpen: boolean
  variant?: 'pill' | 'inline'
}> = ({ isOpen, variant = 'pill' }) => {
  if (variant === 'inline') {
    return (
      <svg
        width='16'
        height='16'
        viewBox='0 0 24 24'
        fill={colors.WHITE}
        xmlns='http://www.w3.org/2000/svg'
        css={css`
          flex-shrink: 0;
          transition: transform 0.2s ease;
          transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
        `}
      >
        <path d='M7 10l5 5 5-5z' />
      </svg>
    )
  }

  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      css={css`
        transition: transform 0.2s ease;
        transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
      `}
    >
      <path
        d='M2.5 4.5L6 8L9.5 4.5'
        stroke={colors.WHITE}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

const FlightSwitcherDropdown: React.FC<FlightSwitcherDropdownProps> = ({
  flights,
  currentFlightId,
  loading,
  onDeleteFlight,
  onFlightSelect,
  onNewFlight,
  variant = 'pill',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null
  )
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
        setConfirmingDeleteId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (!confirmingDeleteId) {
      return
    }
    const dismiss = () => setConfirmingDeleteId(null)
    document.addEventListener('click', dismiss)
    return () => document.removeEventListener('click', dismiss)
  }, [confirmingDeleteId])

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent, flightId: string) => {
      e.stopPropagation()
      setConfirmingDeleteId((prev) => (prev === flightId ? null : flightId))
    },
    []
  )

  const handleConfirmDelete = useCallback(
    async (e: React.MouseEvent, flightId: string) => {
      e.stopPropagation()
      setConfirmingDeleteId(null)
      setDeletingId(flightId)
      try {
        await onDeleteFlight?.(flightId)
      } catch {
        // ignore
      } finally {
        setDeletingId(null)
      }
    },
    [onDeleteFlight]
  )

  const currentFlight = flights.find((f) => f.id === currentFlightId)
  const triggerLabel =
    currentFlight?.display_label ??
    (flights.length === 0 ? 'No Flights' : 'Flight 1')
  const currentFlightDateRange = currentFlight
    ? formatFlightDateRange(currentFlight.start_date, currentFlight.end_date)
    : null

  const handleSelect = (flight: SchedulerFlight) => {
    if (flight.id === currentFlightId) {
      setIsOpen(false)
      return
    }
    setIsOpen(false)
    onFlightSelect?.(flight)
  }

  return (
    <div
      ref={dropdownRef}
      css={css`
        position: relative;
      `}
    >
      {/* Trigger */}
      <button
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        css={
          variant === 'inline'
            ? css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
                width: 100%;
                padding: 8px 8px 8px 16px;
                background: #212935; /* SECONDARY_350 */
                border: 1px solid #313b4b; /* SECONDARY_150 */
                border-radius: 8px;
                cursor: pointer;
                transition: border-color 0.2s ease;
                white-space: nowrap;

                &:hover {
                  border-color: ${colors.NEUTRAL_80};
                }

                &:focus {
                  outline: none;
                  border-color: ${colors.PRIMARY};
                }
              `
            : css`
                display: flex;
                align-items: center;
                gap: 0;
                padding: 0;
                background: ${colors.SECONDARY_300};
                border: 1px solid ${colors.SECONDARY_100};
                border-radius: 20px;
                cursor: pointer;
                transition: border-color 0.2s ease;
                white-space: nowrap;
                overflow: hidden;

                &:hover {
                  border-color: ${colors.NEUTRAL_80};
                }

                &:focus {
                  outline: none;
                  border-color: ${colors.PRIMARY};
                }
              `
        }
      >
        {variant === 'inline' ? (
          <>
            <span
              css={css`
                flex-shrink: 0;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 500;
                color: ${colors.WHITE};
                line-height: 20.3px;
              `}
            >
              {loading ? (
                <Spin
                  indicator={<LoadingOutlined style={spinnerWhiteStyle} spin />}
                />
              ) : (
                triggerLabel
              )}
            </span>

            {!loading && currentFlightDateRange && (
              <span
                css={css`
                  flex-shrink: 0;
                  font-family: 'Inter', sans-serif;
                  font-size: 12px;
                  font-weight: 400;
                  color: ${colors.NEUTRAL_50};
                  line-height: 17.4px;
                `}
              >
                {currentFlightDateRange}
              </span>
            )}

            {!loading && currentFlight && (
              <span
                css={css`
                  flex-shrink: 0;
                  display: inline-flex;
                  align-items: center;
                  gap: 4px;
                  padding: 4px 8px;
                  border-radius: 8px;
                  background: ${colors.NEUTRAL_600};
                  border: 1px solid ${colors.NEUTRAL_50};
                  font-family: 'Inter', sans-serif;
                  font-size: 12px;
                  font-weight: 400;
                  color: ${colors.NEUTRAL_50};
                  line-height: 17.4px;
                `}
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill={colors.NEUTRAL_50}
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path d='M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z' />
                </svg>
                {getFlightStatusLabel(currentFlight.status)}
              </span>
            )}

            {!loading && <DropdownArrowIcon isOpen={isOpen} variant='inline' />}
          </>
        ) : (
          <>
            <span
              css={css`
                padding: 8px 12px;
                font-family: 'Inter', sans-serif;
                font-size: 15px;
                font-weight: 600;
                color: ${colors.WHITE};
                line-height: 20px;
                display: flex;
                align-items: center;
              `}
            >
              {loading ? (
                <Spin
                  indicator={<LoadingOutlined style={spinnerWhiteStyle} spin />}
                />
              ) : (
                triggerLabel
              )}
            </span>

            {!loading && currentFlightDateRange && (
              <>
                <div
                  css={css`
                    width: 1px;
                    height: 16px;
                    background: ${colors.SECONDARY_100};
                    flex-shrink: 0;
                  `}
                />
                <span
                  css={css`
                    padding: 8px 12px;
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 400;
                    color: ${colors.NEUTRAL_80};
                    line-height: 20px;
                    white-space: nowrap;
                  `}
                >
                  {currentFlightDateRange}
                </span>
              </>
            )}

            {!loading && (
              <div
                css={css`
                  width: 1px;
                  height: 16px;
                  background: ${colors.SECONDARY_100};
                  flex-shrink: 0;
                `}
              />
            )}

            {!loading && (
              <div
                css={css`
                  padding: 8px 12px;
                  display: flex;
                  align-items: center;
                  gap: 6px;
                `}
              >
                {currentFlight &&
                  (() => {
                    const badgeColor = getFlightStatusBadgeColor(
                      currentFlight.status
                    )
                    return (
                      <span
                        css={css`
                          display: inline-flex;
                          align-items: center;
                          padding: 2px 8px;
                          border-radius: 4px;
                          font-family: 'Inter', sans-serif;
                          font-size: 12px;
                          font-weight: 500;
                          white-space: nowrap;
                          background: ${badgeColor.bg};
                          color: ${badgeColor.color};
                        `}
                      >
                        {getFlightStatusLabel(currentFlight.status)}
                      </span>
                    )
                  })()}
                <DropdownArrowIcon isOpen={isOpen} />
              </div>
            )}
          </>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          role='listbox'
          css={css`
            position: absolute;
            top: calc(100% + 4px);
            left: 0;
            min-width: fit-content;
            max-height: 320px;
            overflow-y: auto;
            background-color: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_100};
            border-radius: 8px;
            padding: 4px 0;
            z-index: 1000;
            box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.24);
          `}
        >
          {flights.map((flight) => {
            const isCurrent = flight.id === currentFlightId
            const badgeColor = getFlightStatusBadgeColor(flight.status)
            const isConfirming = confirmingDeleteId === flight.id
            const isDeleting = deletingId === flight.id
            const dateRange = formatFlightDateRange(
              flight.start_date,
              flight.end_date
            )

            if (isConfirming || isDeleting) {
              return (
                <div
                  key={flight.id}
                  role='option'
                  aria-selected={false}
                  onClick={(e) => e.stopPropagation()}
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                    padding: 8px 12px;
                    border-left: 3px solid transparent;
                    background: ${colors.SECONDARY_200};
                    animation: fadeIn 0.15s ease-out;

                    @keyframes fadeIn {
                      from {
                        opacity: 0;
                      }
                      to {
                        opacity: 1;
                      }
                    }
                  `}
                >
                  <span
                    css={css`
                      font-family: 'Inter', sans-serif;
                      font-size: 13px;
                      font-weight: 400;
                      color: ${colors.NEUTRAL_50};
                      white-space: nowrap;
                      overflow: hidden;
                      text-overflow: ellipsis;
                    `}
                  >
                    {isDeleting
                      ? `Deleting ${flight.display_label}...`
                      : `Delete ${flight.display_label}?`}
                  </span>

                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      gap: 4px;
                      flex-shrink: 0;
                    `}
                  >
                    {isDeleting ? (
                      <LoadingOutlined spin style={spinnerNeutralStyle} />
                    ) : (
                      <>
                        <button
                          type='button'
                          css={css`
                            background: ${colors.ERROR_200};
                            color: ${colors.NEUTRAL_0};
                            border: none;
                            border-radius: 4px;
                            padding: 4px 10px;
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            font-weight: 600;
                            cursor: pointer;
                            &:hover {
                              background: ${colors.ERROR_300};
                            }
                          `}
                          onClick={(e) => handleConfirmDelete(e, flight.id)}
                        >
                          Delete
                        </button>
                        <button
                          type='button'
                          css={css`
                            background: ${colors.NEUTRAL_500};
                            color: ${colors.NEUTRAL_50};
                            border: none;
                            border-radius: 4px;
                            padding: 4px 8px;
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            font-weight: 500;
                            cursor: pointer;
                            &:hover {
                              background: ${colors.NEUTRAL_400};
                              color: ${colors.NEUTRAL_0};
                            }
                          `}
                          onClick={(e) => {
                            e.stopPropagation()
                            setConfirmingDeleteId(null)
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )
            }

            return (
              <div
                key={flight.id}
                role='option'
                aria-selected={isCurrent}
                onClick={() => handleSelect(flight)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleSelect(flight)
                  }
                }}
                tabIndex={0}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 0;
                  padding: 0 4px 0 0;
                  cursor: pointer;
                  border-left: 3px solid
                    ${isCurrent ? colors.PRIMARY : 'transparent'};
                  background: ${isCurrent
                    ? `${colors.SECONDARY_200}`
                    : 'transparent'};
                  transition: background 0.15s ease;

                  &:hover {
                    background: ${colors.SECONDARY_200};
                  }

                  /* No row-hover rule for .delete-icon-btn: the icon is opaque
                     and red at rest, so there is nothing to reveal — and a row
                     rule here would be (0,3,0) against the button's own (0,2,0)
                     hover and would silently win. */

                  &:focus {
                    outline: none;
                  }

                  &:focus-visible {
                    outline: 2px solid ${colors.PRIMARY};
                    outline-offset: -2px;
                  }
                `}
              >
                {/* Flight name */}
                <span
                  css={css`
                    padding: 8px 12px;
                    font-family: 'Inter', sans-serif;
                    font-size: 13px;
                    font-weight: ${isCurrent ? 600 : 400};
                    color: ${colors.WHITE};
                    line-height: 20px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    min-width: 0;
                  `}
                >
                  {flight.display_label}
                </span>

                {dateRange && (
                  <>
                    <div
                      css={css`
                        width: 1px;
                        height: 16px;
                        background: ${colors.SECONDARY_100};
                        flex-shrink: 0;
                      `}
                    />
                    <span
                      css={css`
                        padding: 8px 12px;
                        font-family: 'Inter', sans-serif;
                        font-size: 12px;
                        font-weight: 400;
                        color: ${colors.NEUTRAL_80};
                        line-height: 20px;
                        white-space: nowrap;
                      `}
                    >
                      {dateRange}
                    </span>
                  </>
                )}

                <div
                  css={css`
                    width: 1px;
                    height: 16px;
                    background: ${colors.SECONDARY_100};
                    flex-shrink: 0;
                  `}
                />

                {/* Status badge */}
                <span
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 8px;
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-family: 'Inter', sans-serif;
                    font-size: 12px;
                    font-weight: 500;
                    white-space: nowrap;
                    background: ${badgeColor.bg};
                    color: ${badgeColor.color};
                    flex-shrink: 0;
                  `}
                >
                  {getFlightStatusLabel(flight.status)}
                </span>

                {/* Delete icon */}
                <div
                  css={css`
                    width: 22px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    margin-left: auto;
                  `}
                >
                  {onDeleteFlight && (
                    <button
                      type='button'
                      className='delete-icon-btn'
                      css={css`
                        background: transparent;
                        border: none;
                        /* Red at REST, not only on hover — the icon reads as
                           destructive before you touch it. Same ERROR_200 the
                           Delete confirm button uses. */
                        color: ${colors.ERROR_200};
                        cursor: pointer;
                        padding: 4px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 14px;
                        border-radius: 4px;
                        transition: color 0.15s ease, opacity 0.15s ease;
                        /* Was 0 — the icon was invisible until row hover, so
                           it was undiscoverable on touch and keyboard users
                           tabbed to a button they could not see. */
                        opacity: 1;

                        /* BRIGHTENS on hover/focus rather than darkening to
                           ERROR_300: the row background lightens to
                           SECONDARY_200 at the same moment, so a darker red
                           would lose contrast exactly when it's interacted
                           with. */
                        &:hover,
                        &:focus-visible {
                          color: ${colors.ERROR_100};
                        }

                        &:focus-visible {
                          outline: 2px solid ${colors.PRIMARY};
                          outline-offset: 1px;
                        }
                      `}
                      onClick={(e) => handleDeleteClick(e, flight.id)}
                      // The row's own onKeyDown preventDefault()s Enter/Space
                      // and switches flights. Without this, tabbing to the trash
                      // icon and pressing Enter changed flight instead of arming
                      // the confirmation. Stop propagation ONLY — calling
                      // preventDefault() here would kill the native activation.
                      onKeyDown={(e) => e.stopPropagation()}
                      aria-label='Delete flight'
                      tabIndex={0}
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </div>
              </div>
            )
          })}

          <div
            css={css`
              height: 1px;
              background: ${colors.SECONDARY_100};
              margin: 4px 0;
            `}
          />

          <div
            role='option'
            onClick={() => {
              setIsOpen(false)
              onNewFlight?.()
            }}
            tabIndex={0}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 10px 12px;
              cursor: pointer;
              border-left: 3px solid transparent;
              transition: background 0.15s ease;

              &:hover {
                background: ${colors.SECONDARY_200};
              }
              &:focus {
                outline: none;
              }
              &:focus-visible {
                outline: 2px solid ${colors.PRIMARY};
                outline-offset: -2px;
              }
            `}
          >
            <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
              <path
                d='M7 1v12M1 7h12'
                stroke={colors.PRIMARY}
                strokeWidth='1.5'
                strokeLinecap='round'
              />
            </svg>
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 13px;
                font-weight: 500;
                color: ${colors.PRIMARY};
              `}
            >
              Add New Flight
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default FlightSwitcherDropdown
